import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Selectable card with radio semantics — the funnel's primary selection
 * control (account type, region, plan). Rendered as a real button so keyboard
 * and screen-reader behaviour come for free.
 */
export function ChoiceCard({
    selected,
    onSelect,
    disabled,
    className,
    children,
    'aria-label': ariaLabel,
}: {
    selected: boolean;
    onSelect: () => void;
    disabled?: boolean;
    className?: string;
    children: ReactNode;
    'aria-label'?: string;
}) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={ariaLabel}
            onClick={onSelect}
            disabled={disabled}
            className={cn(
                'relative w-full rounded-xl border bg-card text-left transition-all duration-150',
                selected
                    ? 'border-primary shadow-[0_10px_28px_-18px] shadow-primary/50 ring-1 ring-primary'
                    : 'border-border shadow-none hover:-translate-y-px hover:border-muted-foreground/40 hover:shadow-md',
                disabled && 'cursor-not-allowed opacity-60 hover:translate-y-0 hover:shadow-none',
                className
            )}
        >
            {children}
        </button>
    );
}

/** The dot that makes a ChoiceCard read as a radio at a glance. */
export function RadioDot({ on }: { on: boolean }) {
    return (
        <span
            aria-hidden
            className={cn(
                'grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-[1.5px] transition-colors',
                on ? 'border-primary bg-primary' : 'border-muted-foreground/40 bg-card'
            )}
        >
            {on && <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
        </span>
    );
}
